// Used in users.component.html, activities-container.component.html, assignment-overview-page.component.html, teacher-classes.component.html,
// teacher-class-assignments.component.html, student-profile.component.html

// Angular
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl } from "@angular/forms";
import { MatMenuTrigger } from "@angular/material/menu";
import { MatSelectChange } from "@angular/material/select";

// Services/Types/Helpers
import { DateFormatterHelper } from "#core/helpers/date-formatter.helper";
import { TimezoneHelper } from "#core/helpers/timezone.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { DateRanges, FilterParams } from "#models/filterParams.interface";
import { AssetTypeHelper, Language, PPUser as User } from "cli-types";

// RXJS/Store
import * as RouterStore from "#core/router-store";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Moment
import { AssignmentHelper } from "#helpers/assignment.helper";
import { SubjectId } from "@carnegie-learning/common-types";
import { assetActivityTypeIds, assetTypeIds } from "@carnegie-learning/cure-types";
import { AssignmentPagination, AssignmentPaginationParameters, CoursePagination, CoursePaginationParameters, LanguageService } from "@carnegie-learning/data-access-angular-passport";
import moment from "moment";

interface FilterParamsIndexable extends FilterParams {
    [index: string]: any; // This isn't ideal, but it clears out a TON of implicit any flags.
}

@Component({
    selector: "filter-in-view",
    templateUrl: "./filter-in-view.component.html",
    styleUrls: ["./filter-in-view.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FilterInViewComponent implements OnInit, OnChanges, OnDestroy {
    filterColumn: string;
    languageNames: { [index: number]: string }; // keyed array of language names.
    languageSet: { [index: number]: Language }; // Languages as returned by the API.
    levelLabel = "Level";
    emcOwnershipLabel = "Passport Content";
    levelRange = null;
    columnDisplayNames: any;
    columnOptDisplayName: any;
    filterForm = this.fb.group({});
    private ngUnsubscribe = new Subject();
    public isMath: boolean;
    baseHiddenKeys = ["isComposite", "dateRanges", "dueDateStatus"];
    hiddenKeys: string[] = [];
    filterChangedBool: boolean;
    guiFilterParams: object;
    dateRangeFormatted: string;
    savedDateRanges: DateRanges | null = null;
    dueDateStatus: string | null;
    filterSectionLabel = {
        name: "Filter",
        fontSize: "14px",
    };
    filterColumnRange: FilterParamsIndexable;

    @Input() header = "Filters";
    @Input() showDateRanges: boolean;
    @Input() language: Language;
    @Input() subjectId: SubjectId;
    @Input() paginationParams:
        | AssignmentPaginationParameters
        | CoursePaginationParameters;
    @Input() pagination: AssignmentPagination | CoursePagination;
    @Input() showFilter: boolean;
    @Input() loading: boolean;
    @Input() excludedFilterOpts: any; // Hide the listed key:value pairs from showing up as a filter option.
    @Input() hiddenFilterChips = []; // Merged with hiddenKeys to prevent certain filters from showing up in the GUI.
    @Input() columnOrder = [];
    // This is an ugly band-aid to satisfy the desire to have lessons split out from other assignables.
    // If someone comes along and asks for a third tab, tell them no and rewrite this crap.
    @Input() apiFilterParams: FilterParamsIndexable;
    @Input() classStartDate: moment.Moment | string;
    @Input() userData: User;
    @Input() cssClass?: string;
    @Input() filterInline?: boolean;

    @Output() apiFilterParamsChanged = new EventEmitter();

    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

    constructor(
        private assignmentHelper: AssignmentHelper,
        private fb: UntypedFormBuilder,
        private languageService: LanguageService,
        private assetTypeHelper: AssetTypeHelper,
        private languageHelper: LanguageHelper,
        private routerStore: Store<RouterStore.RouterStateUrl>,
        private dateFormatterHelper: DateFormatterHelper,
        private timezoneHelper: TimezoneHelper
    ) {}

    ngOnInit() {
        this.filterSectionLabel.name = this.header;
        this.routerStore
            .pipe(select(RouterStore.getRouterState))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((router) => {
                if (router) {
                    if (!this.subjectId && router.state.params.subjectId) {
                        this.subjectId = router.state.params.subjectId; // used to need to be a number parseInt(,10)
                    }
                    this.isMath = this.languageHelper.ifMathLanguage(
                        this.subjectId
                    );
                }
            });

        if (this.paginationParams) {
            this.apiFilterParams = <any>this.paginationParams.filter;
        }

        // Don't bother getting the language list if it is going to be suppressed.
        if (
            this.hiddenFilterChips &&
            this.hiddenFilterChips.find((x) => x === "subjectId")
        ) {
            // These values will get set when the language object gets set.
            this.languageSet = {};
            this.languageNames = {};
        } else {
            this.languageService
                .publicLanguages()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((data) => {
                    const languages: { [index: number]: string } = {},
                        languageSet: { [index: number]: Language } = {};
                    data.forEach(function (lang) {
                        languages[lang.languageID] = lang.languageName;
                        languageSet[lang.languageID] = lang;
                    });
                    this.languageSet = languageSet;
                    this.languageNames = languages;
                });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        let resetColumnNames = false;

        if (changes.language && this.language) {
            this.languageSet = {};
            this.languageNames = {};
            this.languageNames[this.language.languageID] =
                this.language.languageName;

            if (this.languageHelper.ifMathLanguage(this.language.languageID)) {
                // Insert the pretty level list
                this.language = {
                    ...this.language,
                    levelRange: this.languageHelper.getMathPrettyLevelList(),
                };
            }
            this.languageSet[this.language.languageID] = this.language;
            resetColumnNames = true;
        }

        if (changes.paginationParams && this.paginationParams) {
            // this.apiFilterParams = this.paginationParams.filter;
            this.apiFilterParams = <any>this.paginationParams.filter;
            this.guiFilterParams = this.genGuiFilterParams();
            resetColumnNames = true;
        }

        if (changes.pagination && this.pagination) {
            if (
                this.isMath ||
                (changes.language &&
                    changes.language.currentValue &&
                    this.languageHelper.ifMathLanguage(
                        changes.language.currentValue.languageID
                    ))
            ) {
                this.pagination = {
                    ...this.pagination,
                    filter_column_range: {
                        ...this.pagination.filter_column_range,
                        level: this.languageHelper.getMathPrettyLevelColumnRange(),
                    },
                };

                this.apiFilterParams = {
                    ...this.apiFilterParams,
                    level: this.languageHelper.getDBLevelArrayToPrettyArray(
                        this.apiFilterParams.level
                    ),
                };
            }
            if (this.paginationParams.filter) {
                const keyList = Object.keys(this.paginationParams.filter);
                for (const key of keyList) {
                    if (
                        this.paginationParams.filter &&
                        this.hiddenKeys.indexOf(key) === -1
                    ) {
                        const tempValue =
                            key === "level" &&
                            this.apiFilterParams[key] &&
                            this.apiFilterParams[key].length
                                ? this.apiFilterParams[key].map(
                                      this.cleanUpLevels
                                  )
                                : this.apiFilterParams[key];
                        this.filterForm.addControl(
                            key,
                            new UntypedFormControl()
                        );
                        this.filterForm.get(key)?.setValue(tempValue);
                    }
                }
            }

            if (
                this.apiFilterParams.dateRanges &&
                this.apiFilterParams.dateRanges.from &&
                this.apiFilterParams.dateRanges.to
            ) {
                let dateRanges = this.apiFilterParams.dateRanges;
                dateRanges = {
                    ...dateRanges,
                    from: this.timezoneHelper.convertServerDateToUserTZ(
                        dateRanges.from,
                        this.userData.timezone
                    ),
                };
                dateRanges = {
                    ...dateRanges,
                    to: this.timezoneHelper.convertServerDateToUserTZ(
                        dateRanges.to,
                        this.userData.timezone
                    ),
                };
                this.apiFilterParams = { ...this.apiFilterParams, dateRanges };
                this.savedDateRanges = this.apiFilterParams.dateRanges;
                const from = this.dateFormatterHelper.formatDateMMDDYY(
                    dateRanges.from
                );
                const to = this.dateFormatterHelper.formatDateMMDDYY(
                    dateRanges.to
                );
                this.dateRangeFormatted = from + " - " + to;
            }

            this.dueDateStatus = "";
            if (this.apiFilterParams.dueDateStatus) {
                this.dueDateStatus = this.apiFilterParams.dueDateStatus;
            }
        }

        if (resetColumnNames) {
            this.setColumnDisplayNames();
        }
    }

    cleanUpLevels(index: any): string | number {
        //I can tell what this is doing, and I'd rather not mess with it.
        return !isNaN(parseFloat(index)) ? parseFloat(index) : index;
    }

    genGuiFilterParams() {
        // need to reset every time
        this.hiddenKeys = [...this.baseHiddenKeys];
        // Merge the arrays while eliminating duplicates.
        if (this.hiddenFilterChips && this.hiddenFilterChips.filter) {
            this.hiddenKeys = this.hiddenKeys.concat(
                this.hiddenFilterChips.filter((x) =>
                    this.hiddenKeys.every((y) => y !== x)
                )
            );
        }

        const results: { [index: string]: string[] } = {};

        const vm = this;
        Object.keys(vm.apiFilterParams).forEach(function (result) {
            if (vm.hiddenKeys.indexOf(result) > -1) {
                // This is a hidden key, skip it.
                return;
            }

            const filterOpts = vm.filterChipsOpts(result);
            if (filterOpts.length > 0) {
                results[result] = filterOpts;
            }
        });

        return results;
    }

    getFilterKeys() {
        return Object.keys(this.guiFilterParams);
    }

    /**
     * eg: columns are keys and options are values in the array
     * {
     *      type: [new, old ...]
     *      level: [1,2,3, ...]
     * }
     */
    addFilter(column: string, value: string) {
        // Convert display value filter parameters to the parameters the API expects.
        let filterColumn = this.apiFilterParams[column];
        if (filterColumn === undefined) {
            filterColumn = [];
        }

        filterColumn = [...filterColumn, value];

        this.apiFilterParams = {
            ...this.apiFilterParams,
            [column]: filterColumn,
        };

        this.apiFilterParamsChanged.emit(
            this.mathiaConvertPrettyLevelToDB(this.apiFilterParams)
        );
    }

    isInverseFilter(value: string) {
        if (typeof value === "string") {
            return value.substring(0, 1) === "!";
        }
        return false;
    }

    toggleFilter(column: string, value: string) {
        const filterColumnOptions = this.apiFilterParams[column];
        if (filterColumnOptions === undefined) {
            return;
        }

        const index = filterColumnOptions.indexOf(value);
        const clone = [...filterColumnOptions];
        if (index === -1) {
            return;
        }

        if (typeof value === "string" && value.substring(0, 1) === "!") {
            clone[index] = value.substring(1, value.length);
        } else {
            clone[index] = "!" + value;
        }

        this.apiFilterParams = { ...this.apiFilterParams, [column]: clone };

        this.apiFilterParamsChanged.emit(
            this.mathiaConvertPrettyLevelToDB(this.apiFilterParams)
        );
    }

    removeFilter(column: string, value: string): void {
        // Convert value to api filter value.
        const filterColumn = this.apiFilterParams[column];
        if (filterColumn === undefined) {
            return;
        }

        if (this.apiFilterParams[column].length > 1) {
            // More than 1 option was selected for this filter key, only remove the value
            let filterColumnOptions = this.apiFilterParams[column];
            const index = filterColumnOptions.indexOf(value);
            const clone = [...filterColumnOptions];
            delete clone[index];
            filterColumnOptions = clone;
            filterColumnOptions.splice(index, 1); // remove element from index
            this.apiFilterParams = {
                ...this.apiFilterParams,
                [column]: filterColumnOptions,
            };
        } else {
            // Only 1 option is selected for filter key, delete the filter key
            const clone = Object.assign({}, this.apiFilterParams);
            delete clone[column];
            this.apiFilterParams = clone;
        }

        this.apiFilterParamsChanged.emit(
            this.mathiaConvertPrettyLevelToDB(this.apiFilterParams)
        );
    }

    clearFilters() {
        this.apiFilterParams = {
            assetTypeId: "",
            status: {},
            dueDateStatus: "",
            dateRanges: {
                from: "",
                to: "",
            },
            level: [],
            levelIds: [],
        }; // update apiFilterParams as empty object instead of null.
        this.apiFilterParamsChanged.emit(
            this.mathiaConvertPrettyLevelToDB(this.apiFilterParams)
        );
    }

    filterChipsOpts(filterColumn: string) {
        return this.scrubFilterOptions(filterColumn, this.apiFilterParams);
    }

    filterColumns() {
        if (this.pagination === undefined) {
            return [];
        }

        if (this.paginationParams.filter) {
            let keyList = Object.keys(this.paginationParams.filter);

            // Put the keys in the correct order by calculating the intersection between this list
            // And the given order list.
            if (this.columnOrder && this.columnOrder.length > 0) {
                keyList = this.columnOrder.filter(function (v) {
                    // iterate over the array
                    return keyList.indexOf(v) > -1;
                });
            }

            let newList: string[] = [];

            for (const key of keyList) {
                if (
                    this.paginationParams.filter &&
                    this.hiddenKeys.indexOf(key) === -1
                ) {
                    // only include columns that have options, api sends empty arrays sometimes
                    newList = [...newList, key];
                } else {
                    // empty option array detected, skip
                    continue;
                }

                if (this.isMath) {
                    const unitIndex = newList.indexOf("unit");
                    if (unitIndex > -1) {
                        newList.splice(unitIndex, 1);
                    }
                }
            }
            return newList;
        }
    }

    selectionChanged(event: MatSelectChange, filterColumn: string) {
        this.filterChangedBool = true;

        if (this.apiFilterParams.status) {
            // Handle Date Range picker statuses
            const statusArray = event.value;
            let newArray: string[] = [];
            statusArray.forEach((status: string) => {
                newArray.push(status);
            });
            (<any>this.apiFilterParams.status).forEach((status: string) => {
                if (
                    status === "overdue" ||
                    status === "dueThisWeek" ||
                    status === "dueToday"
                ) {
                    newArray.push(status);
                }
            });
            if (!statusArray.length) {
                newArray = [];
            }

            this.apiFilterParams = {
                ...this.apiFilterParams,
                [filterColumn]: newArray,
            };
        } else {
            this.apiFilterParams = {
                ...this.apiFilterParams,
                [filterColumn]: event.value,
            };
        }
    }

    filterChanged(event: MatSelectChange, filterColumn: string) {
        if (this.filterChangedBool) {
            this.apiFilterParamsChanged.emit(
                this.mathiaConvertPrettyLevelToDB(this.apiFilterParams)
            );
        }
    }

    filterColumnOpts(filterColumn: string) {
        if (this.paginationParams) {
            return this.scrubFilterOptions(
                filterColumn,
                this.pagination.filter_column_range
            );
        }
        return [];
    }

    scrubFilterOptions(filterColumn: string, parentObj: FilterParamsIndexable) {
        const filterColumnOpts = parentObj[filterColumn];

        if (!filterColumnOpts || filterColumnOpts.length === 0) {
            return [];
        }

        let newList: string[] = [];
        for (const key of filterColumnOpts) {
            if (
                this.excludedFilterOpts &&
                this.excludedFilterOpts[filterColumn] &&
                (this.excludedFilterOpts[filterColumn].indexOf(key) !== -1 ||
                    this.excludedFilterOpts[filterColumn].indexOf(
                        key.slice(1)
                    ) !== -1)
            ) {
            } else {
                newList = [...newList, key];
            }
        }
        newList = this.assignmentHelper.checkAssignableTypeException(newList);
        return newList;
    }

    setSubMenuFilterColumn(filterColumn: string) {
        if (filterColumn === "status") {
            // special case: only return status options used currently
            return [
                "pending",
                "submitted",
                "evaluated",
                "practice",
                "scheduled",
            ];
        }
        this.filterColumn = filterColumn;
    }

    disableSubMenuFilter(filterColumn: string, filterOption: string) {
        if (this.apiFilterParams[filterColumn]) {
            const filterOptions = this.apiFilterParams[filterColumn];
            return (
                filterOptions.indexOf(filterOption) !== -1 ||
                filterOptions.indexOf("!" + filterOption) !== -1
            );
        } else {
            // column wasn't selected yet so keep enabled
            return false;
        }
    }

    apiColumnToDisplayName(column: string) {
        if (
            this.columnDisplayNames &&
            !this.columnDisplayNames.hasOwnProperty(column)
        ) {
            return column;
        }

        return this.columnDisplayNames[column];
    }

    setColumnDisplayNames() {
        if (this.language) {
            this.levelLabel = this.language.levelLabel;
            this.levelRange = this.language.levelRange;
            if (this.languageHelper.ifMathLanguage(this.language.languageID)) {
                this.emcOwnershipLabel = "Carnegie Learning";
            } else {
                this.emcOwnershipLabel =
                    this.language.languageName + " Content";
            }
        } else if (
            this.languageSet &&
            this.apiFilterParams["languageID"] &&
            this.apiFilterParams["languageID"].length === 1
        ) {
            const languageID = parseInt(
                this.apiFilterParams["languageID"][0],
                10
            );
            const language = this.languageSet[languageID];

            this.levelLabel = language?.levelLabel || "";
            this.levelRange = language?.levelRange || "";
            if (this.languageHelper.ifMathLanguage(languageID)) {
                this.emcOwnershipLabel = "Carnegie Learning";
            } else {
                this.emcOwnershipLabel = language?.languageName + " Content";
            }
        } else {
            this.levelLabel = "Level";
            this.emcOwnershipLabel = "Passport Content";
            this.levelRange = null;
        }

        this.columnDisplayNames = {
            languageID: "Program",
            languageName: "Program",
            assignableType: "Type",
            unit: "Unit",
            level: this.levelLabel,
            subType: "Resource",
            lesson: "Lesson",
            visibility: "Shared With",
            tier: "Tier",
            assignmentName: "Title",
            status: "Status",
            ownerRelationship: "Authored By",
            assignedInCourse: "Assigned In Class",
            spineTitle: "Title",
            spineType: "Type",
            type: "Type",
            roleLevel: "Role",
        };
        const assetActivityTypeIdsObjs = Object.values(
            assetActivityTypeIds
        ).reduce(
            (obj: { [index: string]: string }, type) => (
                (obj[type] = this.assetTypeHelper.getCapTypeName(type)), obj
            ),
            {}
        );
        const assetTypeIdsObjs = Object.values(assetTypeIds).reduce(
            (obj: { [index: string]: string }, type) => (
                (obj[type] = this.assetTypeHelper.getCapTypeName(type)), obj
            ),
            {}
        );
        const assignableType = {
            ...assetActivityTypeIdsObjs,
            ...assetTypeIdsObjs,
        };

        this.columnOptDisplayName = {
            languageID: this.languageNames,
            assignableType,
            visibility: {
                self: "Only Me",
                none: "Passport Community",
                district: "My District",
                school: "My School",
            },
            tier: { lite: "Lite", premium: "Premium" },
            status: {
                pending: "Assigned",
                submitted: "Submitted",
                evaluated: "Evaluated",
                scheduled: "Scheduled",
                inProgress: "In Progress",
            },
            ownerRelationship: {
                self: "Educator Created",
                school: "Teachers in My School",
                district: "Teachers in My District",
                none: "Passport Community",
                emc: this.emcOwnershipLabel,
            },
            spineType: {
                pe: "Student Materials",
                te: "Teacher Materials",
                explore: "iCulture",
                ancillary: "Ancillary",
                edulastic: "Edulastic",
                iculture: "iCulture",
                crv: "Close Reading Video",
                expand: "eReaders",
            },
            type: {
                0: "Video",
                1: "Image",
            },
            roleLevel: {
                1: "Distributor",
                2: "District Admin",
                3: "School Admin",
                4: "Teacher",
                5: "Student",
            },
        };

        if (this.levelRange) {
            this.columnOptDisplayName["level"] = this.levelRange;
        }
    }

    apiColumnOptToDisplayName(column: string, option: string) {
        // Strip leading ! off.
        if (typeof option === "string" && option.substring(0, 1) === "!") {
            option = option.substring(1, option.length);
        }

        if (
            !this.columnOptDisplayName.hasOwnProperty(column) ||
            this.columnOptDisplayName[column] === undefined ||
            !this.columnOptDisplayName[column].hasOwnProperty(option)
        ) {
            return option;
        }

        return this.columnOptDisplayName[column][option];
    }

    hasIcon(filterColumn: string) {
        return filterColumn === "assignableType";
    }

    hasStatus(filterColumn: string) {
        return filterColumn === "status";
    }

    getIconPath(shortAssignableType: string) {
        return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
            shortAssignableType
        );
    }

    mathiaConvertPrettyLevelToDB(apiFilters: any) {
        if (
            this.languageHelper.ifMathLanguage(this.subjectId) &&
            apiFilters.hasOwnProperty("level") &&
            apiFilters.level !== undefined
        ) {
            return {
                ...apiFilters,
                level: this.languageHelper.getPrettyLevelArrayToValueToDBArray(
                    apiFilters.level
                ),
            };
        } else {
            return apiFilters;
        }
    }

    onDateInputChanged(event: Event): void {
        if (
            event?.type === "change" &&
            (<HTMLFormElement>event.target)?.value === ""
        ) {
            this.getDateRanges(null);
        }
    }
    getDateRanges(
        event: null | [DateRanges, { selectedQuickOption: string }]
    ): void {
        //TODO: This tupple may likely be needed elsewhere.
        // event null means, date Reset button has chosen or Date Input value empty
        // set Dates to filter
        const dateRangesKey = "dateRanges";
        const statusKey = "status";
        const dueDateKey = "dueDateStatus";
        let dueDateStatus = "";

        if (event) {
            this.apiFilterParams = {
                ...this.apiFilterParams,
                [dateRangesKey]: event[0],
            };
            if (event[1] && event[1].selectedQuickOption) {
                dueDateStatus = event[1].selectedQuickOption;
                const statusArray: string[] = [];
                let dueDateStatusValue = "";
                // get a clean status array without due date statuses
                if (this.apiFilterParams.status) {
                    (<any>this.apiFilterParams.status).forEach(
                        (status: string) => {
                            if (
                                status === "scheduled" ||
                                status === "pending" ||
                                status === "inProgress" ||
                                status === "submitted" ||
                                status === "evaluated"
                            ) {
                                statusArray.push(status);
                            }
                        }
                    );
                }
                if (dueDateStatus) {
                    dueDateStatusValue = dueDateStatus;
                }
                this.apiFilterParams = {
                    ...this.apiFilterParams,
                    [statusKey]: statusArray,
                    [dueDateKey]: dueDateStatusValue,
                };
            }
        } else {
            this.apiFilterParams = {
                ...this.apiFilterParams,
                [dateRangesKey]: event,
            };
            const statusArray: string[] = [];
            if (this.apiFilterParams.status) {
                (<any>this.apiFilterParams.status).forEach((status: string) => {
                    if (
                        status === "scheduled" ||
                        status === "pending" ||
                        status === "inProgress" ||
                        status === "submitted" ||
                        status === "evaluated"
                    ) {
                        statusArray.push(status);
                    }
                });
            }
            this.apiFilterParams = {
                ...this.apiFilterParams,
                [statusKey]: statusArray,
            };
        }
        this.apiFilterParamsChanged.emit(this.apiFilterParams);
        // close menu
        this.trigger.closeMenu();
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
